import { BOOKING_URL, IMG } from '../../lib/constants';
import { Star, MapPin, ArrowRight, Award, Clock } from 'lucide-react';

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-36 sm:pt-40 overflow-hidden bg-brand-bg"
    >
      {/* Dark gold radial glow background */}
      <div
        aria-hidden
        className="absolute inset-0 bg-brand-gradient pointer-events-none"
      />
      {/* Subtle gold strip top */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-gold-strip opacity-50"
      />

      <div className="container-prep relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center pb-16">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-gold/10 border border-brand-gold/30 rounded-full backdrop-blur-sm mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-medium text-brand-gold">
              Downtown Vancouver · Burrard St
            </span>
          </div>

          <h1 data-testid="hero-h1" className="heading-1 max-w-4xl">
            YUMI Lash Lift Vancouver —
            <span className="italic font-light text-brand-gold block">Natural, lifted lashes</span>
            <span className="italic font-light text-brand-gold block">that last 8–12 weeks.</span>
          </h1>

          <p className="body-lg mt-7 max-w-xl text-brand-cream/75">
            Authentic YUMI Keratin Lash Lift &amp; InLei Lash Filler® at Prép Beauty Parlour — 1305 Burrard St, Downtown Vancouver. Certified YUMI artists since 2014.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-now-button"
              className="btn-primary group"
            >
              Book Your Lash Lift
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#pricing"
              data-testid="hero-pricing-link"
              className="btn-secondary"
            >
              Explore Pricing
            </a>
          </div>

          {/* Stat strip — balayage style */}
          <div className="mt-14 flex flex-wrap items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center">
                <Award size={16} className="text-brand-gold" />
              </div>
              <div>
                <div className="font-serif text-2xl text-brand-cream leading-none">3,300+</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1">5-Star Reviews</div>
              </div>
            </div>
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center">
                <Clock size={16} className="text-brand-gold" />
              </div>
              <div>
                <div className="font-serif text-2xl text-brand-cream leading-none">75 min</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1">Session Time</div>
              </div>
            </div>
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-full bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center">
                <Star size={16} className="text-brand-gold fill-brand-gold" />
              </div>
              <div>
                <div className="font-serif text-2xl text-brand-cream leading-none">From $125</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1">Lift + Tint</div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero visual */}
        <div className="lg:col-span-5 relative animate-fade-in">
          <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto">
            <div
              aria-hidden
              className="absolute -inset-2 rounded-[2rem] bg-brand-gold/20 blur-2xl"
            />
            <div className="relative rounded-[2rem] overflow-hidden border border-brand-gold/30 shadow-[0_30px_80px_-20px_rgba(201,169,110,0.35)]">
              <img
                src={IMG.hero}
                alt="Best YUMI lash lift in Vancouver — natural lifted lashes at Prép Beauty Parlour"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-bg/40 via-transparent to-transparent" />
            </div>

            {/* floating cert badge */}
            <div className="absolute left-2 sm:-left-8 bottom-6 sm:bottom-8 bg-brand-bg/90 backdrop-blur-md border border-brand-gold/30 px-4 sm:px-5 py-3 sm:py-4 rounded-2xl shadow-2xl rotate-[-3deg]">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-brand-gold mb-0.5">
                Certified
              </div>
              <div className="font-serif text-base sm:text-lg leading-tight text-brand-cream">
                YUMI <span className="text-brand-gold">+</span> InLei
              </div>
            </div>
            <div className="absolute right-2 sm:-right-6 top-6 sm:top-10 bg-brand-gold text-brand-ink px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl shadow-xl rotate-[3deg]">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] opacity-70">From</div>
              <div className="font-serif text-xl sm:text-2xl leading-none">$125</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-brand-muted text-[10px] tracking-[0.4em] uppercase">
        Scroll
      </div>
    </section>
  );
};

export default Hero;
